/**
 * B-7233: stale-edit protection for the web app, kept out of api.ts (a god file).
 *
 * The server returns `payload_version` with every estimate it sends. A line
 * edit sends the last one back as `base_version`; if a chat (MCP) approval or
 * another save changed the estimate since, the server answers 409 and the page
 * shows "This estimate changed, reload".
 */
import type { ApiError } from '../types';

/** Fired on window when a save is refused because the estimate changed. */
export const ESTIMATE_CHANGED_EVENT = 'estimate-changed';
export const ESTIMATE_CHANGED_MESSAGE = 'This estimate changed, reload';

const payloadVersions = new Map<string, string>();

/** Remember the version in any response body (or job result) that carries one. */
export function rememberPayloadVersion(body: any): void {
  for (const b of [body, body?.result]) {
    if (b && typeof b.parse_hash === 'string' && typeof b.payload_version === 'string') {
      payloadVersions.set(b.parse_hash, b.payload_version);
    }
  }
}

/** `{ base_version }` to spread into an edit request, or `{}` when none was received. */
export function baseVersion(parseHash: string): { base_version?: string } {
  const v = payloadVersions.get(parseHash);
  return v ? { base_version: v } : {};
}

/** Convert an axios error into an Error that carries the HTTP status and any structured code. */
export function buildApiError(error: any, baseUrl: string): ApiError {
  const status: number | undefined = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const structured = typeof detail === 'object' && detail ? detail : null;
  let message: string;
  if (!error?.response) {
    message =
      'Network error or CORS issue. Make sure the backend is reachable at ' +
      `${baseUrl} and it allows requests from this origin.`;
  } else {
    message =
      (structured ? structured.message : detail) ||
      error.response?.data?.message ||
      error.message ||
      'An unexpected backend error occurred.';
  }
  const err = new Error(message) as ApiError & { code?: string };
  if (status !== undefined) err.status = status;
  if (structured?.code) err.code = structured.code;
  if (status === 409 && err.code === 'estimate_changed') {
    err.message = ESTIMATE_CHANGED_MESSAGE;
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(ESTIMATE_CHANGED_EVENT));
  }
  return err;
}
